import React, { useState, useEffect } from "react";
import {
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Row,
    Col,
    Modal,
    ModalHeader,
    ModalBody,
} from "reactstrap";
import Session from '../auth/Session';

const stateOptions = [
    "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
    "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
    "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
    "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
    "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

const normalizeState = (value) => String(value || "").trim().toUpperCase();

// <Input type="date"> needs a bare yyyy-MM-dd string; the API returns ISO datetimes.
const toDateInput = (value) => (value ? String(value).slice(0, 10) : "");

const getDriverStates = (driver) => {
    const states = [
        ...(driver?.states || []),
        ...(driver?.stateAssignments || []).map(a => a.state),
    ];

    if (!states.length && driver?.state) {
        states.push(driver.state);
    }

    return [...new Set(states.map(normalizeState).filter(Boolean))].sort();
};

const DriverForm = ({ driver, apiBaseUrl, onSuccess, onCancel }) => {
    const [formData, setFormData] = useState({
        id: 0,
        contractType: "",
        subCustomerId: null,
        businessName: "",
        driverName: "",
        address: "",
        city: "",
        state: "",
        zipCode: "",
        phone: "",
        email: "",
        dateOfBirth: null,
        driversLicenseState: "",
        driversLicenseExpiration: null,
        insuranceDecPageExpiration: null,
        bankAccountNumber: "",
        bankRoutingNumber: "",
        accountDepositType: 0,
        ssn: "",
        fein: "",
        location: "",
        locationManager: "",
        effectiveStartDate: null,
        endDate: null,
        rehireEligible: null,
        reasonForLeaving: "",
        active: true,
        states: [],
        emergencyContact: "",
        emergencyContactNumber: "",
        emergencyContactRelationship: "",
    });

    const [subCustomers, setSubCustomers] = useState([]);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [routeCount, setRouteCount] = useState(0);
    const [saving, setSaving] = useState(false);
    const subCustomersApi = "/api/subcustomers";

    useEffect(() => {
        if (driver) {
            setFormData({
                ...driver,
                states: getDriverStates(driver),
            });
        }
    }, [driver]);

    useEffect(() => {
        const loadData = async () => {
            const token = await Session.getToken();
            const res = await fetch(subCustomersApi, { headers: { Authorization: `Bearer ${token}` } });
            if (res.ok) {
                const data = await res.json();
                setSubCustomers(data);
            }
        }
        loadData();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();

        const isEdit = driver && driver.id > 0;
        const wasActive = (driver?.active ?? true) !== false;
        const willBeInactive = formData.active === false;

        if (isEdit && wasActive && willBeInactive) {
            try {
                const token = await Session.getToken();
                const res = await fetch(`${apiBaseUrl}/${formData.id}/assigned-route-count`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    const count = data?.count ?? 0;
                    if (count > 0) {
                        setRouteCount(count);
                        setConfirmOpen(true);
                        return;
                    }
                }
            } catch (err) {
                // If the count lookup fails, fall through and let the save proceed.
                console.error("Failed to fetch assigned route count", err);
            }
        }

        await doSave();
    };

    const selectedStates = getDriverStates(formData);

    const handleDriverStateChange = (state, checked) => {
        setFormData((prev) => {
            const current = getDriverStates(prev);
            const next = checked
                ? [...current, state]
                : current.filter(value => value !== state);

            return {
                ...prev,
                states: [...new Set(next)].sort(),
                subCustomerId: checked ? prev.subCustomerId : prev.subCustomerId,
            };
        });
    };

    const subCustomerMatchesDriverStates = (subCustomer) =>
        selectedStates.includes(normalizeState(subCustomer?.state));

    const doSave = async () => {
        setSaving(true);
        try {
            const method = driver ? "PUT" : "POST";
            const url = driver ? `${apiBaseUrl}/${formData.id}` : apiBaseUrl;
            const token = await Session.getToken();

            const res = await fetch(url, {
                method,
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify(formData),
            });

            setConfirmOpen(false);
            if (res.ok) {
                onSuccess();
            } else {
                alert("Error saving driver");
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Form onSubmit={handleSubmit}>
            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label>Driver Name</Label>
                        <Input
                            value={formData.driverName}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, driverName: e.target.value }))
                            }
                        />
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label>Business Name</Label>
                        <Input
                            value={formData.businessName}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, businessName: e.target.value }))
                            }
                        />
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label>Email</Label>
                        <Input
                            type="email"
                            value={formData.email}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, email: e.target.value }))
                            }
                        />
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label>Phone</Label>
                        <Input
                            value={formData.phone}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, phone: e.target.value }))
                            }
                        />
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col md={12}>
                    <FormGroup>
                        <Label>Address</Label>
                        <Input
                            name="address"
                            value={formData.address}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, address: e.target.value }))
                            }
                        />
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col md={4}>
                    <FormGroup>
                        <Label>City</Label>
                        <Input name="city"
                            value={formData.city}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, city: e.target.value }))
                            }
                        />
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup>
                        <Label>State</Label>
                        <Input type="select" name="state"
                            value={formData.state || ""}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, state: e.target.value }))
                            }
                        >
                            <option value="">— select —</option>
                            {stateOptions.map((s) => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </Input>
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup>
                        <Label>Postal Code</Label>
                        <Input
                            name="zipCode"
                            value={formData.zipCode}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, zipCode: e.target.value }))
                            }
                        />
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md={12}>
                    <FormGroup>
                        <Label>Route/Sub-Customer State Assignments</Label>
                        <div className="border rounded p-2" style={{ maxHeight: 160, overflowY: "auto" }}>
                            <Row>
                                {stateOptions.map((state) => (
                                    <Col xs={4} sm={3} md={2} key={state}>
                                        <FormGroup check className="mb-1">
                                            <Label check>
                                                <Input
                                                    type="checkbox"
                                                    checked={selectedStates.includes(state)}
                                                    onChange={(e) => handleDriverStateChange(state, e.target.checked)}
                                                />{" "}
                                                {state}
                                            </Label>
                                        </FormGroup>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md={12}>
                    <FormGroup>
                        <Label for="subCustomerId">Customer {selectedStates.length === 0 && ( <>(Select a sub-customer state)</> )}</Label>
                        <Input
                            id="subCustomerId"
                            name="subCustomerId"
                            type="select"
                            value={formData.subCustomerId || ""}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, subCustomerId: e.target.value }))
                            }
                            disabled={selectedStates.length === 0}
                        >
                            <option value="">-- Select Sub-Customer --</option>
                            {subCustomers.filter(subCustomerMatchesDriverStates).map(c => (
                                <option key={c.id} value={c.id}>
                                    {c.name}
                                </option>
                            ))}
                        </Input>
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md={4}>
                    <FormGroup>
                        <Label for="endDate">End Date</Label>
                        <Input
                            id="endDate"
                            name="endDate"
                            type="date"
                            value={toDateInput(formData.endDate)}
                            onChange={(e) => {
                                const value = e.target.value || null;
                                // Setting an end date terminates the driver, so
                                // reflect that by unchecking Active (mirrors the
                                // server, and triggers the route-unassign confirm).
                                setFormData((p) => ({
                                    ...p,
                                    endDate: value,
                                    active: value ? false : p.active,
                                }));
                            }}
                        />
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup>
                        <Label for="rehireEligible">Rehire Eligibility</Label>
                        <Input
                            id="rehireEligible"
                            name="rehireEligible"
                            type="select"
                            value={formData.rehireEligible === true ? "true" : formData.rehireEligible === false ? "false" : ""}
                            onChange={(e) => {
                                const v = e.target.value;
                                setFormData((p) => ({
                                    ...p,
                                    rehireEligible: v === "" ? null : v === "true",
                                }));
                            }}
                        >
                            <option value="">— not specified —</option>
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                        </Input>
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup check className="mb-3 mt-4">
                        <Label check>
                            <Input
                                id="active"
                                name="active"
                                type="checkbox"
                                checked={formData.active !== false}
                                onChange={(e) =>
                                    setFormData((p) => ({ ...p, active: e.target.checked }))
                                }
                            />{" "}
                            Active
                        </Label>
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md={12}>
                    <FormGroup>
                        <Label for="reasonForLeaving">Reason for Leaving</Label>
                        <Input
                            id="reasonForLeaving"
                            name="reasonForLeaving"
                            type="textarea"
                            rows={2}
                            value={formData.reasonForLeaving || ""}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, reasonForLeaving: e.target.value }))
                            }
                        />
                    </FormGroup>
                </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2">
                <Button color="secondary" onClick={onCancel} disabled={saving}>
                    Cancel
                </Button>
                <Button color="primary" type="submit" disabled={saving}>
                    {driver ? "Update" : "Create"}
                </Button>
            </div>

            <Modal isOpen={confirmOpen} toggle={() => setConfirmOpen(false)}>
                <ModalHeader toggle={() => setConfirmOpen(false)}>Inactivate Driver</ModalHeader>
                <ModalBody>
                    <p>
                        This driver is currently assigned to {routeCount} route{routeCount === 1 ? "" : "s"}.
                        Marking them inactive will unassign the driver from those route{routeCount === 1 ? "" : "s"}.
                        Historical schedule, invoice, and pay sheet data will be preserved.
                    </p>
                    <p className="mb-0">Do you want to continue?</p>
                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <Button color="secondary" onClick={() => setConfirmOpen(false)} disabled={saving}>
                            Cancel
                        </Button>
                        <Button color="warning" onClick={doSave} disabled={saving}>
                            {saving ? "Saving..." : "Inactivate"}
                        </Button>
                    </div>
                </ModalBody>
            </Modal>
        </Form>
    );
};

export default DriverForm;
